
import type { ShopifyCartResponse } from "./shopify";
import type Tags from './Tags';
import Log from './Log';
import OneSignal from './types/OneSignal';
import Shopify, { getShopifyCustomerId } from './shopify';
import { areTagsEqual } from './Tags';
import TagsCache from './TagCache';
import Outcomes, { Outcome, buildRevenueOutcome, areOutcomesEqual } from './outcomes';
import { isOnPurchaseConfirmationPage, isOnPasswordProtectedPage, isOnProductPage } from './utils';
import { SettingsManager, Settings } from "./settings";
import InStockHelper from "./helpers/inStockHelper";
/**
 * How frequently we'll fetch the Shopify /cart.js API.
 */
const QUERY_SHOPIFY_CART_DURATION = 15000; // 15 seconds

/**
 * How frequently we'll check the current url if we are on a product page
 */
const QUERY_SHOPIFY_PRODUCT_PAGE_DURATION = 3000; // 3 seconds

/**
 * An enum to represent the initialization state of the SDK.  We should only be initializing when the state is NotInitialized.
 */
enum InitializationState {
  NotInitialized = 0,
  Initializing,
  Complete,
  Skipped,
}

/**
 * An object to help track state of the OneSignal Shopify SDK.
 */
interface State {
  initializationState: InitializationState;
  appId?: string;
  tags?: Tags;
  revenueOutcome?: Outcome;
  settings?: Settings;
  productPath?: string;
}

/**
 * The base class that handles the shopify logic.
 */
export default class OneSignalShopifySDK {
  private static state: State = {
    initializationState: InitializationState.NotInitialized,
  };

  /**
   * Initialize the OneSignal Shopify SDK and start updating player tags based on a user's cart.
   *
   * @param appId The OneSignal appId we're initializing.
   */
  public static initialize(appId: string): void {
    if (this.state.initializationState !== InitializationState.NotInitialized) {
      return;
    }

    this.state.initializationState = InitializationState.Initializing;
    this.state.appId = appId;

    // initialize the logger
    Log.setLevel("debug");

    // we don't initialize on the password protected page since we can't load the service worker.
    // the service worker goes through the shopify application proxy which fails to load if the
    // store is password protected and the user hasn't entered in their password yet.
    if (isOnPasswordProtectedPage()) {
      Log.debug("Currently on password protected page. Skipping Initialization of OneSignal SDK.");
      this.state.initializationState = InitializationState.Skipped;
      return;
    }

    // load the tags from the cache
    this.state.tags = TagsCache.load();

    // load the existing revenue outcome from local storage
    this.state.revenueOutcome = Outcomes.getRevenueOutcome();

    // initialize OneSignal SDK
    window.OneSignal = window.OneSignal || [];
    window.OneSignal.push(this.completeInitialization.bind(this));
  }

  /**
   * The callback for the OneSignal initialization process.
   */
  private static async completeInitialization(): Promise<void> {
    if (!this.state.appId) {
      Log.error("AppID not set, unable to initialize OneSignal SDK.");
      return;
    }

    Log.debug("Initializing OneSignal SDK.");

    // initialize the SDK with the App ID now
    await (window.OneSignal as OneSignal).init({ appId: this.state.appId });

    // we have successfully initialized the library.
    this.state.initializationState = InitializationState.Complete;

    // The settings script is requested through CloudFlare (before hitting our servers)
    // which will cache the response
    this.state.settings = await SettingsManager.load(this.state.appId);

    // Send the purchase outcome, if necessary. This should be executed once per page load and as soon as
    // initialization is complete. This method is responsible for actually sending the outcome data up to
    // the server.  It also will do checks to verify it should actually send the outcome.
    await this.sendRevenueOutcomeIfNecessary();

    // Display the in stock prompt, if the conditions apply. It will also poll for url changes in
    // case there is a new variant on the product page.
    await this.queryShopifyProductOutOfStockPage();

    // query the shopify cart and update the tags necessary
    await this.queryShopifyCartAndUpdateData();
  }

  /**
   * Query the Shopify /cart.js API then poll it again.
   */
  private static async queryShopifyCartAndUpdateData(): Promise<void> {
    const response = await Shopify.getCart();
    if (!response.success) {
      Log.error("Failed to query the Shopify cart.", response.error);
    }

    // update the tags with the response, if it was successful
    if (response.success) {
      this.updateRevenueOutcomeCache(response.body);
      await this.updatePlayerTags(response.body);
    }

    // we want to periodically query the cart while we're on this page.
    setTimeout(this.queryShopifyCartAndUpdateData.bind(this), QUERY_SHOPIFY_CART_DURATION);
  }

  /**
   * Send the revenue outcome, if we're on the purchase confirmation page.
   *
   * This also confirms the value is revenue outcome value >= 0 as well as the
   * user being on the purchase confirmation page.
   */
  private static async sendRevenueOutcomeIfNecessary(): Promise<void> {
    Log.debug("Attempting to send revenue outcome.", this.state.revenueOutcome);

    // if the current outcome doesn't have a value, then quickly return
    if (!this.state.revenueOutcome || this.state.revenueOutcome.value <= 0) {
      Log.debug("Not sending outcome, no revenue outcome value.");
      return;
    }

    // we only send this outcome, if we're on the purchase confirmation page
    if (!isOnPurchaseConfirmationPage()) {
      Log.debug("Not sending outcome, not on purchase confirmation page.");
      return;
    }

    Log.debug("Sending revenue outcome to server.", this.state.revenueOutcome);

    // create a "clone" of the value so we can clear the cached values
    const outcome = buildRevenueOutcome(this.state.revenueOutcome.value);

    // clear the cached value and reset the state
    Outcomes.setRevenueValue(0);
    this.state.revenueOutcome = Outcomes.getRevenueOutcome();

    await (window.OneSignal as OneSignal).sendOutcome(outcome.id, outcome.value);
  }

  /**
   * Update the cache of the revenue outcome.
   *
   * @param response The success response from the Shopify /cart.js API.
   */
  private static updateRevenueOutcomeCache(response: ShopifyCartResponse): void {
    // shopify represents the values in cents
    const value = response.original_total_price;
    const updatedRevenueOutcome = buildRevenueOutcome(value);

    // only update these values if they're different.  This just prevents spending CPU
    // cycles writing to localStorage when we're able to compare the data in memory.
    if(!areOutcomesEqual(this.state.revenueOutcome, updatedRevenueOutcome)) {
      // this will cache the value in localStorage
      Outcomes.setRevenueValue(updatedRevenueOutcome.value);
      this.state.revenueOutcome = updatedRevenueOutcome;
    }
  }

  /**
   * Update the player tags with OneSignal Web SDK, if they have changed.
   *
   * @param response The success response from the Shopify /cart.js API.
   */
  private static async updatePlayerTags(response: ShopifyCartResponse): Promise<void> {
    const itemsInCart: number = response.items.length;

    // We update the external user id only when we add an item to a cart
    // The cart token will remain the same if more items are added
    if (itemsInCart > 0) {
      const externalID = await (window.OneSignal as OneSignal).getExternalUserId();
      if (externalID !== response.token) {
        await (window.OneSignal as OneSignal).setExternalUserId(response.token);
        Log.debug(`ExternalID changed from ${externalID} to ${response.token}`);
      }
    }

    // update the player tags
    const tags: Tags = {
      item_count: itemsInCart,
      item_name: undefined,
      item_image_url: undefined,
      customer_id: getShopifyCustomerId(),
    };

    if (tags.item_count > 0) {
      const item = response.items[tags.item_count - 1];
      tags.item_name = item.title;
      tags.item_image_url = item.image;
    }

    // have we already sent these tags up?
    if (areTagsEqual(tags, this.state.tags)) {
      Log.debug("Tags are unchanged, not sending update.", tags);
      return;
    }

    Log.debug("Updating Player Tags", tags);

    // update the player tags
    await (window.OneSignal as OneSignal).sendTags(tags, ((tags: Tags): void => {
      Log.debug("Successfully updated player tags.", tags);
      this.state.tags = tags;
      TagsCache.cache(tags);
    }).bind(this, tags));
  }

  /**
   * Queries the current path if we are on a product page and polls again.
   * If the current path changed while on the product page it will check if the conditions
   * apply to display an in stock prompt.
   */
  private static async queryShopifyProductOutOfStockPage(): Promise<void> {
    if (isOnProductPage()) {
      const currentPath: string = window.location.href;

      if (this.state.productPath !== currentPath) {
        Log.debug("Product page url changed, will check conditions to display in-stock prompt");
        this.state.productPath = currentPath;

        // check conditions and display in stock prompt
        await InStockHelper.attemptToDisplayInStock(this.state.settings);
      } else {
        Log.debug("Product page url has not changed, will not attempt to display in-stock prompt");
      }

      setTimeout(this.queryShopifyProductOutOfStockPage.bind(this), QUERY_SHOPIFY_PRODUCT_PAGE_DURATION);
    } else {
      Log.debug("Not on a product page, will not poll for variant changes");
      this.state.productPath = undefined;
    }
  }
}
